import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, userAPI, setAccessToken, setRefreshToken, getRefreshToken, clearTokens } from '../services/api';
import { mapBackendUserToFrontend } from '../utils/dataMappers';

// Primary fields are camelCase (post-mapper). A small set of snake_case aliases
// are retained for components that may read the user object during the session-restore
// window before mapBackendUserToFrontend has run (e.g. partial localStorage restore).
interface User {
  id: number;
  userCode?: string;
  name: string;
  fullName?: string;
  email: string;
  phoneNumber?: string;
  /** @deprecated use phoneNumber — present during session-restore window */
  phone?: string;
  role: string;
  postalCode?: string;
  state?: string;
  district?: string;
  city?: string;
  blockTehsil?: string;
  panCardNo?: string;
  image?: string;
  address?: string;
  pincode?: string;
  isEmailVerified?: boolean;
  isBlocked?: boolean;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;

  // Ambassador
  isAmbassador?: boolean;
  ambassadorStatus?: 'PENDING' | 'APPROVED' | 'REJECTED' | null;
  ambassadorAppliedAt?: string;
  ambassadorApprovedAt?: string;
  ambassadorId?: string;
  ambassadorRecord?: any;

  // KYC Fields
  donorType?: string;
  /** @deprecated use donorType — present during session-restore window */
  donor_type?: string;
  kycStatus?: string;
  kycSubmittedAt?: string;
  // ambassador_name/phone resolved via ambassadors FK join — read-only from API
  ambassadorName?: string;
  ambassadorPhone?: string;
  whatsappNumber?: string;
  fullAddress?: string;
  country?: string;
  isWhatsappSameAsMobile?: boolean | number;
  referencerId?: string;

  // Citizen / Resident
  photoIdType?: string;
  photoIdNumber?: string;
  passportSizePhoto?: string;
  idDocumentScan?: string;
  idDocumentScanBack?: string;
  addressProofType?: string;
  addressProofNumber?: string;
  addressDocumentScan?: string;
  addressDocumentScanBack?: string;
  addressProofSameAsPhotoId?: boolean | number;
  alumniVerified?: boolean | number;

  // Organization
  orgName?: string;
  orgEmail?: string;
  orgPan?: string;
  orgGovtId?: string;
  orgRepName?: string;
  orgRepEmail?: string;
  orgRepPhone?: string;
  orgRepWhatsapp?: string;
  isOrgWhatsappSameAsMobile?: boolean | number;
  orgPanScan?: string;
  orgGovtIdScan?: string;
  declarationAgreed?: boolean | number;
  isReferencer?: boolean;
  referencerRecord?: any;

  // Cross-project profile sharing consent
  consentForProfileSharing?: boolean | number;
  consentGivenAt?: string | null;
}


interface AuthContextType {
  user: User | null;
  token: string | null;
  logout: () => void;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isAmbassador: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(async () => {
    try {
      const rfToken = getRefreshToken();
      await authAPI.logout(rfToken);
    } catch (error) {
      // Ignore logout errors
    }
    setUser(null);
    setToken(null);
    clearTokens();
    window.location.href = '/login';
  }, []);

  const fetchFullUserProfile = useCallback(async (token: string, currentUser: User) => {
    try {
      const response = await userAPI.getProfile();
      const userData = response.data?.user || response.data;

      if (userData) {
        const fullUser = mapBackendUserToFrontend(userData, currentUser);
        setUser(fullUser);
        // Store only minimal identity in localStorage to limit PII exposure.
        // Full profile is always fetched fresh from the API on each session restore.
        try {
          localStorage.setItem('user', JSON.stringify({ id: fullUser.id, userCode: fullUser.userCode, name: fullUser.name, email: fullUser.email, role: fullUser.role }));
        } catch (_) { /* private browsing / quota exceeded — non-critical, session restore won't work on next reload */ }
      }
    } catch (error) {
      const status = (error as any)?.response?.status;
      if (status === 401) {
        // Import toast dynamically to avoid circular deps — use dispatchEvent instead
        window.dispatchEvent(new CustomEvent('auth:session-expired'));
        logout();
        return;
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    const handler = () => {
      setUser(null);
      setToken(null);
      clearTokens();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    };

    const expiredHandler = () => {
      window.dispatchEvent(new CustomEvent('toast:error', { detail: 'Your session has expired. Please sign in again.' }));
      setUser(null);
      setToken(null);
      clearTokens();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    };

    window.addEventListener('auth:unauthorized', handler);
    window.addEventListener('auth:session-expired', expiredHandler);
    return () => {
      window.removeEventListener('auth:unauthorized', handler);
      window.removeEventListener('auth:session-expired', expiredHandler);
    };
  }, []);

  useEffect(() => {
    const currentPath = window.location.pathname;
    const isOAuthCallback = ['/oauth2/callback', '/callback'].includes(currentPath);

    if (isOAuthCallback) {
      setLoading(false);
      return;
    }

    // Session restore: access token lives in memory (lost on page refresh), so we
    // check for a stored user profile and a valid refresh token and re-hydrate.
    const savedUser = localStorage.getItem('user');
    const savedRefreshToken = getRefreshToken();

    if (savedUser && savedRefreshToken) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        setLoading(false);
        // Kick off a background token refresh to get a fresh access token into memory.
        authAPI.refreshToken(savedRefreshToken)
          .then((res) => {
            const { token: newToken, refreshToken: newRefreshToken } = res.data;
            setAccessToken(newToken);
            setRefreshToken(newRefreshToken);
            setToken(newToken);
            fetchFullUserProfile(newToken, parsedUser);
          })
          .catch(() => {
            // Refresh failed — session is expired. Clear and redirect.
            clearTokens();
            setUser(null);
            setToken(null);
            if (currentPath !== '/login') {
              window.location.href = '/login';
            }
          });
      } catch (err) {
        clearTokens();
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [fetchFullUserProfile]);



  const value = {
    user,
    token,
    logout,
    setUser,
    setToken,
    // isAuthenticated: treat the user as authenticated during the refresh window
    // (loading=true AND a saved user/refresh-token exist) to prevent ProtectedRoute
    // from flashing a redirect to /login on every page reload.
    isAuthenticated: !!token || (loading && !!user),
    isAdmin: user?.role === 'ADMIN',
    isAmbassador: user?.role === 'AMBASSADOR' || user?.ambassadorStatus === 'APPROVED' || user?.isAmbassador === true,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
